import React, { useCallback, useMemo, useState } from 'react';
import { isEqual, sumBy } from 'lodash';

import { Box, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import Edit from '@mui/icons-material/Edit';

import { formatCurrencyFloat, formatPercentageString } from '../../../../../../shared/utils/numberFormatters';

import ReimbursementEditModal from '../edit-modal/ReimbursementEditModal';
import {
  CommercialReimbursement,
  PercentOfCurrentYearLiabilityReimbursement,
} from 'types/underwriting/income-approach';
import { Field } from 'mobx-react-form';
import NumericInput from 'components/shared/inputs/NumericInput';
import { DEFAULT_EXPENSES_TITLES } from 'shared/constants/expenses';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { renderCheckboxCell } from 'components/underwriting/utils/cell-renders';
import { observer } from 'mobx-react-lite';

type PercentOfCurrentTaxYearLiabilityReimbursementTableProps = {
  reimbursementField: Field;
  onUpdate: (reimbursement: CommercialReimbursement) => void;
  onDelete: () => void;
};

const PercentOfCurrentTaxYearLiabilityReimbursementTable: React.FC<PercentOfCurrentTaxYearLiabilityReimbursementTableProps> =
  observer(({ reimbursementField, onUpdate, onDelete }) => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const unitReimbursementsField: Field = reimbursementField.$('unitReimbursements');
    const vcLossField: Field = reimbursementField.$('vcLoss');
    const grossVCLossField: Field = reimbursementField.$('grossVCLoss');
    const grossAnnualValueField: Field = reimbursementField.$('grossAnnualValue');
    const effectiveAnnualValueField: Field = reimbursementField.$('effectiveAnnualValue');

    const openEditModal = () => {
      setIsEditModalOpen(true);
    };
    const closeEditModal = () => {
      setIsEditModalOpen(false);
    };

    const updateReimbursement = (updatedReimbursement: CommercialReimbursement) => {
      onUpdate(updatedReimbursement);
      closeEditModal();
    };
    const deleteReimbursement = () => {
      onDelete();
    };

    const recalculateReimbursementValues = useCallback(() => {
      const reimbursement: PercentOfCurrentYearLiabilityReimbursement =
        reimbursementField.value as PercentOfCurrentYearLiabilityReimbursement;

      const newGrossAnnualValue = sumBy(
        reimbursement.unitReimbursements.filter((unitReimbursement) => unitReimbursement.enabled),
        (unitReimbursement) => unitReimbursement.annual || 0
      );
      grossAnnualValueField.set(newGrossAnnualValue);

      const newGrossVCLossValue = (newGrossAnnualValue || 0) * ((reimbursement.vcLoss || 0) / 100);
      grossVCLossField.set(newGrossVCLossValue);

      const newEffectiveAnnualValue = (newGrossAnnualValue || 0) - (newGrossVCLossValue || 0);
      effectiveAnnualValueField.set(newEffectiveAnnualValue);
    }, [reimbursementField.value, grossAnnualValueField, grossVCLossField, effectiveAnnualValueField]);

    const onRowUpdate = useCallback(
      ({ id, ...newRow }: any, oldRow: any) => {
        if (newRow.isTotalRow || isEqual(newRow, oldRow)) {
          return oldRow;
        }

        let updatedNewRow = newRow;
        let updatedUnitReimbursements = (unitReimbursementsField.value || []).map((unitReimbursement: any) => {
          if (unitReimbursement.unitId === oldRow.unitId) {
            const annualLessAdminFee =
              ((newRow.currentYearLiability || 0) * (newRow.percentOfCurrentYearLiability || 0)) / 100;
            const grossAdminFee = ((newRow.currentYearLiability || 0) * (newRow.adminFeePercentage || 0)) / 100;
            updatedNewRow = {
              ...newRow,
              annualLessAdminFee,
              grossAdminFee,
              annual: annualLessAdminFee + grossAdminFee,
            };
            return updatedNewRow;
          }
          return unitReimbursement;
        });

        unitReimbursementsField.set(updatedUnitReimbursements);
        recalculateReimbursementValues();

        return updatedNewRow;
      },
      [recalculateReimbursementValues, unitReimbursementsField]
    );

    const onRowEnabledChange = useCallback(
      (row: any, checked: boolean) => {
        const targetField = unitReimbursementsField
          .map((field: Field) => field)
          .find((field) => {
            return field.value.unitId === row.unitId;
          });

        if (targetField) {
          targetField.set({ ...targetField.value, enabled: checked });
          recalculateReimbursementValues();
        }
      },
      [unitReimbursementsField, recalculateReimbursementValues]
    );

    const gridColumns: GridColDef[] = useMemo(() => {
      const reimbursement: PercentOfCurrentYearLiabilityReimbursement =
        reimbursementField.value as PercentOfCurrentYearLiabilityReimbursement;
      const adminFeeKnown = reimbursement.adminFeeKnown;
      const annualLessAdminFeeColumn: GridColDef = {
        field: 'annualLessAdminFee',
        headerName: 'Gross Less Admin Fee',
        minWidth: 200,
        flex: 1,
        align: 'right',
        editable: false,
        type: 'number',
        valueFormatter: (value: any) => {
          if (value == null || value === '') return '';
          return formatCurrencyFloat(value);
        },
        renderCell: (params: any) => {
          if (params.row.isTotalRow) {
            return '';
          }
          if (params.value == null || params.value === '') return '';
          return formatCurrencyFloat(params.value);
        },
      };
      const adminFeePercentageColumn: GridColDef = {
        field: 'adminFeePercentage',
        headerName: 'Admin Fee %',
        minWidth: 200,
        flex: 1,
        align: 'right',
        editable: true,
        type: 'number',
        valueFormatter: (value: any) => {
          if (value == null || value === '') return '';
          return formatPercentageString(value);
        },
        renderCell: (params: any) => {
          if (params.row.isTotalRow) {
            return '';
          }
          if (params.value == null || params.value === '') return '';
          return formatPercentageString(params.value);
        },
      };
      const grossAdminFeeColumn: GridColDef = {
        field: 'grossAdminFee',
        headerName: 'Gross Admin Fee',
        minWidth: 200,
        flex: 1,
        align: 'right',
        editable: false,
        type: 'number',
        valueFormatter: (value: any) => {
          if (value == null || value === '') return '';
          return formatCurrencyFloat(value);
        },
        renderCell: (params: any) => {
          if (params.row.isTotalRow) {
            return '';
          }
          if (params.value == null || params.value === '') return '';
          return formatCurrencyFloat(params.value);
        },
      };
      return [
        {
          field: 'enabled',
          headerName: '',
          width: 50,
          align: 'center',
          editable: true,
          type: 'boolean',
          renderCell: (params) => renderCheckboxCell(params, onRowEnabledChange),
        },
        {
          field: 'tenant',
          headerName: 'Tenant Name',
          minWidth: 200,
          flex: 1,
          align: 'left',
          editable: false,
          type: 'string',
          renderCell: (params: any) => {
            if (params.row.isTotalRow) {
              return 'Total:';
            }
            return params.value;
          },
        },
        {
          field: 'currentYearLiability',
          headerName: 'Current Year Liability',
          minWidth: 200,
          flex: 1,
          align: 'right',
          editable: false,
          type: 'number',
          valueFormatter: (value: any) => {
            if (value == null || value === '') return '';
            return formatCurrencyFloat(value);
          },
          renderCell: (params: any) => {
            if (params.row.isTotalRow) {
              return '';
            }
            if (params.value == null || params.value === '') return '';
            return formatCurrencyFloat(params.value);
          },
        },
        {
          field: 'percentOfCurrentYearLiability',
          headerName: '% of Current Year Liability',
          minWidth: 200,
          flex: 1,
          align: 'right',
          editable: true,
          type: 'number',
          valueFormatter: (value: any) => {
            if (value == null || value === '') return '';
            return formatPercentageString(value);
          },
          renderCell: (params: any) => {
            if (params.row.isTotalRow) {
              return '';
            }
            if (params.value == null || params.value === '') return '';
            return formatPercentageString(params.value);
          },
        },
        ...(adminFeeKnown ? [annualLessAdminFeeColumn, adminFeePercentageColumn, grossAdminFeeColumn] : []),
        {
          field: 'annual',
          headerName: 'Annual Reimbursement',
          minWidth: 200,
          flex: 1,
          align: 'right',
          editable: false,
          type: 'number',
          valueFormatter: (value: any) => {
            if (value == null || value === '') return '';
            return formatCurrencyFloat(value);
          },
          renderCell: (params: any) => {
            if (params.value == null || params.value === '') return '';
            return formatCurrencyFloat(params.value);
          },
        },
      ];
    }, [onRowEnabledChange, reimbursementField.value]);

    const renderUnitReimbursementTable = () => {
      const reimbursement: PercentOfCurrentYearLiabilityReimbursement =
        reimbursementField.value as PercentOfCurrentYearLiabilityReimbursement;
      const unitReimbursementRows = reimbursement.unitReimbursements.map((unitReimbursement) => {
        return {
          ...unitReimbursement,
          //   grossForecastedValue:
          //     expenseForecasts.find((expenseForecast) => expenseForecast.key === reimbursementField.value.expenseCategory)
          //       ?.concludedValue || 0,
          id: unitReimbursement.unitId,
        };
      });

      const rowsWithIds = [
        ...unitReimbursementRows,
        {
          id: 'annual-total',
          tenant: 'Total:',
          annual: reimbursement.grossAnnualValue,
          isTotalRow: true,
        },
      ];

      return (
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={rowsWithIds}
            columns={gridColumns}
            processRowUpdate={onRowUpdate}
            onProcessRowUpdateError={(error) => {
              console.log({ error });
            }}
            hideFooter
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            disableColumnSorting
            disableColumnResize
            getRowHeight={() => 52}
            getRowId={(row) => {
              return row.unitId || 'annual-total';
            }}
            rowBufferPx={52 * 20}
            getCellClassName={(params) => {
              if (
                params.field === 'enabled' ||
                params.field === 'percentOfCurrentYearLiability' ||
                params.field === 'adminFeePercentage'
              ) {
                return '';
              }

              return 'disabled-cell';
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #e0e0e0',
                borderRight: '1px solid #f0f0f0',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                borderBottom: '2px solid #e0e0e0',
              },

              '& .MuiDataGrid-row[data-id="annual-total"]': {
                backgroundColor: 'rgb(245, 245, 245)',
                '& .MuiDataGrid-cell': {
                  backgroundColor: 'rgb(245, 245, 245)',
                  color: '#666',
                  fontWeight: 'bold',
                },
              },
              '& .disabled-cell': {
                backgroundColor: 'rgb(245, 245, 245)',
                color: '#666',
                fontWeight: 'normal',
              },
              '& .MuiDataGrid-cell input[type="number"]::-webkit-outer-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0,
              },
              '& .MuiDataGrid-cell input[type="number"]::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
                margin: 0,
              },
              '& .MuiDataGrid-cell input[type="number"]': {
                '-moz-appearance': 'textfield',
              },
            }}
            isCellEditable={(params) => {
              if (
                params.field === 'enabled' ||
                params.field === 'percentOfCurrentYearLiability' ||
                params.field === 'adminFeePercentage'
              ) {
                return true;
              }

              return false;
            }}
          />
        </Box>
      );
    };

    const renderVCLossSection = () => {
      const reimbursement: PercentOfCurrentYearLiabilityReimbursement =
        reimbursementField.value as PercentOfCurrentYearLiabilityReimbursement;

      const expenseType = DEFAULT_EXPENSES_TITLES[reimbursement.expenseCategory] || reimbursement.expenseCategory;
      return (
        <Grid container justifyContent="space-between">
          <Grid item xs={4} padding="0px 16px">
            <NumericInput
              {...vcLossField.bind()}
              disabled
              label="VC Loss %"
              size="small"
              margin="normal"
              allowNegative={false}
              decimalScale={2}
              max={100}
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={4} padding="0px 16px">
            <NumericInput
              {...grossVCLossField.bind()}
              label="Gross V/C Loss"
              size="small"
              margin="normal"
              allowNegative={false}
              decimalScale={2}
              fullWidth
              disabled
              thousandSeparator
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={4} padding="0px 16px">
            <NumericInput
              {...effectiveAnnualValueField.bind()}
              label={`Effective ${expenseType} Reimbursement`}
              size="small"
              margin="normal"
              allowNegative={false}
              decimalScale={2}
              fullWidth
              disabled
              thousandSeparator
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      );
    };

    return (
      <React.Fragment>
        <Grid container direction="row" alignItems="center">
          <Typography>{(reimbursementField.value as PercentOfCurrentYearLiabilityReimbursement).name}</Typography>
          <IconButton onClick={openEditModal}>
            <Edit color="primary" />
          </IconButton>
          <IconButton onClick={deleteReimbursement}>
            <DeleteOutlined color="error" />
          </IconButton>
        </Grid>
        {isEditModalOpen && (
          <ReimbursementEditModal
            reimbursementField={reimbursementField}
            onSave={updateReimbursement}
            onCancel={closeEditModal}
            isCreateModal={false}
          />
        )}
        <Grid container width={'100%'}>
          <Grid item xs={12}>
            {renderUnitReimbursementTable()}
            {renderVCLossSection()}
          </Grid>
        </Grid>
      </React.Fragment>
    );
  });

export default PercentOfCurrentTaxYearLiabilityReimbursementTable;
